<script lang="ts" setup>
    import { onMounted, ref, reactive, computed } from 'vue';
    import Combo from "../../components/Combo.vue";
    import TextInput from "../../components/TextInput.vue";
    import SearchPatient from '../../components/Search/SearchPatient.vue';
    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import ScheduleMessage from "../../components/Communication/ScheduleMessage.vue";  
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { IRecallStatusItem, IRecallResourceItem, IRecallAppointmentTypeItem, IRecallSetupResult, IRecallSetupSaveInputs } from '../../lib/Models/RecallModel';
    import { ISearchPatientItem, ISearchPatientKey } from '../../lib/Models/SearchModel';
    import { ButtonDisplaySize } from "../../Enums/UiEnums";
    import { PrivilegeType, AppScreens } from "../../Enums/ChoiceEnums";
    import { recall, patient } from '../../lib/ChoiceApi';
    import { usePrivilegesStore } from '../../lib/Stores/PrivilegesStore'    
    import { initPinia } from '../../lib/PiniaMgr';
    import { popAlert } from '../../lib/Notification';  
    import '../../../css/choice.css';
    
    const emit = defineEmits<{
        (e: "onRecallSetupSaveClick", recallReminderId: number): void,
    }>();
    defineExpose({loadSetup});

    const formData = reactive<{
        recallReminderId?: number,
        setup: IRecallSetupResult,
        keySearch: number
    }>({
        setup: <IRecallSetupResult> {patientId: 0},
        keySearch: 0
    });

    const defaultPatientKey = ref<ISearchPatientKey>();
    const scheduleMsg = ref<typeof ScheduleMessage>();
    const privilegesStore = usePrivilegesStore(initPinia());

    const props = defineProps<{
        statusList: IRecallStatusItem[],
        resourceList: IRecallResourceItem[],
        appointmentTypeList: IRecallAppointmentTypeItem[],
        recallReminderId?: number,
        recallPatientId?: number
    }>();

    onMounted(async () => {
        await privilegesStore.getScreens([AppScreens.RecallSetup]);
        if (props.recallReminderId) {
            loadSetup(props.recallReminderId);
        } else if (props.recallPatientId) {
            loadPatient(props.recallPatientId);
        }
    });

    const setupPhoneHome = computed<string>(() => {
        if (!formData.setup)
            return "";
        return (formData.setup.homePhone ?? "") != "" ? `${formData.setup.homePhone} ` : "";
    });
    const setupPhoneHomeExt = computed<string>(() => {
        if (!formData.setup)
            return "";
        return (formData.setup.homePhoneExt ?? "") != "" ? `x${formData.setup.homePhoneExt} ` : "";
    });
    const setupPhoneWork = computed<string>(() => {
        if (!formData.setup)
            return "";
        return (formData.setup.workPhone ?? "") != "" ? `${formData.setup.workPhone} ` : "";
    });
    const setupPhoneWorkExt = computed<string>(() => {
        if (!formData.setup)
            return "";
        return (formData.setup.workPhoneExt ?? "") != "" ? `x${formData.setup.workPhoneExt} ` : "";
    });
    const setupPhoneOther = computed<string>(() => {
        if (!formData.setup)
            return "";
        return (formData.setup.otherPhone ?? "") != "" ? `${formData.setup.otherPhone} ` : "";
    });
    const setupPhoneOtherExt = computed<string>(() => {
        if (!formData.setup)
            return "";
        return (formData.setup.otherPhoneExt ?? "") != "" ? `x${formData.setup.otherPhoneExt} ` : "";
    });
    const writePrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.RecallSetup] >= PrivilegeType.ReadUpdateWrite ? true : false);
    const updatePrivilege = computed<boolean>(() => privilegesStore.screens[AppScreens.RecallSetup] >= PrivilegeType.ReadUpdate ? true : false);
    const patientEnabled = computed<boolean>(() => ((formData.recallReminderId ?? 0) > 0 || props.recallPatientId) ? false : true);
    const savedEnabled = computed<boolean>(() => (((formData.recallReminderId ?? 0) > 0 && updatePrivilege) || ((formData.recallReminderId ?? 0) == 0 && writePrivilege)) ? true : false);
    const chartNumber = computed<string>(() => formData.setup.patientId ?? 0 > 0 ? formData.setup.patientId.toString() : "");

    async function onPatientSearch(searchResultItem: ISearchPatientItem) {
        if (!searchResultItem)
            return;
        formData.setup.patientId = searchResultItem.patient_id ?? 0;
        formData.setup.birthDate = searchResultItem.birth_date ?? "";

        const respDemo = await patient.demographics(formData.setup.patientId);
        if (!respDemo)
            return;
        formData.setup.homePhone = respDemo.phoneHome;
        formData.setup.homePhoneExt = respDemo.phoneHomeExt;
        formData.setup.workPhone = respDemo.phoneWork;
        formData.setup.workPhoneExt = respDemo.phoneWorkExt;
        formData.setup.otherPhone = respDemo.phoneOther;
        formData.setup.otherPhoneExt = respDemo.phoneOtherExt;
    }

    async function loadSetup(recallReminderId?: number) {
        formData.recallReminderId = recallReminderId;
        clearSetup();
        if (!recallReminderId)
            return;
        formData.setup = await recall.setup(recallReminderId ?? 0);
        defaultPatientKey.value = {
            patientId: formData.setup.patientId,
            display: formData.setup.patientName
        }
    }

    async function loadPatient(recallPatientId?: number) {
        if (!recallPatientId)
            return;
        const respDemo = await patient.demographics(recallPatientId);
        if (!respDemo)
            return;
        formData.setup.patientId = respDemo.patientId;
        formData.setup.homePhone = respDemo.phoneHome;
        formData.setup.homePhoneExt = respDemo.phoneHomeExt;
        formData.setup.workPhone = respDemo.phoneWork;
        formData.setup.workPhoneExt = respDemo.phoneWorkExt;
        formData.setup.otherPhone = respDemo.phoneOther;
        formData.setup.otherPhoneExt = respDemo.phoneOtherExt;
        formData.setup.birthDate = respDemo.birthDate;        
        defaultPatientKey.value = {
            patientId: formData.setup.patientId,
            display: formData.setup.patientName
        }
    }

    function clearSetup() {
        formData.keySearch++;
        formData.setup = <IRecallSetupResult> {patientId: 0}
        defaultPatientKey.value = {
            patientId: undefined
        }
    }

    async function onSave() {
        if (formData.setup.patientId == 0) {
            await popAlert("Please enter a patient.", "Save");
            return;
        }

        const post_data = <IRecallSetupSaveInputs> {
            recallReminderId: formData.recallReminderId,
            patientId: formData.setup.patientId,
            appointmentTypeId: formData.setup.appointmentTypeId,
            dueDate: formData.setup.dueDate,
            recallStatusCode: formData.setup.recallStatusCode,
            comments: formData.setup.comments,
            resourceEntityId: formData.setup.resourceEid
        };
        const respSave = await recall.save(post_data);
        if (!respSave.recallReminderId)
            return;
        formData.recallReminderId = respSave.recallReminderId;
        emit("onRecallSetupSaveClick", respSave.recallReminderId);

        if (props.recallPatientId || props.recallReminderId)
            scheduleMsg.value?.performAction({action: "CloseRecallSetup"});
    }

</script>

<template>
    <div class="row">
        <div class="col">
            <div class="row">
                <div class="col">
                    <SearchPatient
                        :key="formData.keySearch"
                        :defaultSearchKey="defaultPatientKey" 
                        :addToBaseClass="'label-isalus-small'"
                        :disabled="!patientEnabled"
                        @onChange="(val) => onPatientSearch(val)">
                    </SearchPatient>
                </div>
            </div>
            <div class="row mt-2">
                <div class="col label-isalus-small">
                    Chart #
                </div>
                <div class="col label-isalus-small">
                    DOB
                </div>
                <div class="col label-isalus-small">
                </div>
            </div>
            <div class="row">
                <div class="col label-isalus-small">
                    {{ chartNumber }}
                </div>
                <div class="col label-isalus-small">
                    {{ formData.setup?.birthDate  }}
                </div>
                <div class="col label-isalus-small">
                </div>
            </div>
            <div class="row mt-2">
                <div class="col label-isalus-small">
                    Home
                </div>
                <div class="col label-isalus-small">
                    Work
                </div>
                <div class="col label-isalus-small">
                    Other
                </div>
            </div>
            <div class="row">
                <div class="col label-isalus-small">
                    {{ setupPhoneHome }}{{ setupPhoneHomeExt }}
                </div>
                <div class="col label-isalus-small">
                    {{ setupPhoneWork }}{{ setupPhoneWorkExt }}
                </div>
                <div class="col label-isalus-small">
                    {{ setupPhoneOther }}{{ setupPhoneOtherExt }}
                </div>
            </div>
        </div>
        <div class="col">
            <div class="row mt-2">
                <div class="col label-isalus-small">
                    Comments
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <TextInput 
                        v-model:text-value="formData.setup.comments" 
                        :rows="5"
                        :maxLength="1500"
                        :cssFramework = "'vanilla'" />
                </div>
            </div>
        </div>
    </div>
    <div class="row mt-2">
        <div class="col label-isalus-small">
            Appointment Type
        </div>
        <div class="col label-isalus-small">
            Resource
        </div>
    </div>
    <div class="row">
        <div class="col">
            <Combo 
                :optionList="props.appointmentTypeList"
                optionValue="appointmentTypeId"
                optionDescription="appointmentName"
                :includeBlank="true"
                v-model:selected="formData.setup.appointmentTypeId" />
        </div>
        <div class="col">
            <Combo 
                :optionList="props.resourceList"
                optionValue="resourceEid"
                optionDescription="resourceName"
                optionHeader="resourceType"
                :includeHeader="true"
                :includeBlank="true"
                v-model:selected="formData.setup.resourceEid" />
        </div>
    </div>
    <div class="row mt-2">
        <div class="col label-isalus-small">
            Due Date
        </div>
        <div class="col label-isalus-small">
            Date Created
        </div>
    </div>
    <div class="row">
        <div class="col">
            <DatePickerNoTZ 
                v-model:value="formData.setup.dueDate" 
                :cssFramework="'vanilla'"
                :clearable="false"
                :centerCalendarPopup="true" />
        </div>
        <div class="col label-isalus-small mt-2">
            {{  formData.setup?.dateCreated }}
        </div>
    </div>
    <div class="row mt-2">
        <div class="col label-isalus-small">
            Recall Status
        </div>
        <div class="col label-isalus-small">
            Appointment Date
        </div>
    </div>
    <div class="row">
        <div class="col">
            <Combo 
                :optionList="props.statusList"
                optionValue="recallStatusCode"
                :includeBlank="true"
                v-model:selected="formData.setup.recallStatusCode" />        
        </div>
        <div class="col">
            <div class="row">
                <div class="col label-isalus-small mt-2">
                    {{  formData.setup?.appointmentDate }}
                </div>
                <div class="col d-flex justify-content-end">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Save" 
                        text="Save"
                        :size="ButtonDisplaySize.Small"
                        :disabled="!savedEnabled" 
                        :performAction="onSave" />
                </div>
            </div>
        </div>
    </div>
    <ScheduleMessage ref="scheduleMsg"/>  
</template>

